import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  consolidateAnnouncement,
  previewConsolidation,
  type ConsolidationLead,
  type ConsolidationPlan,
} from '../lib/api/announcements.js';

interface Props {
  announcementId: string;
}

/**
 * Same-session consolidation (BC8 2026-09-07). Shows the operator which of
 * the issuer's existing same-day articles this filing would be folded into
 * — on their slugs — which filing leads, and which of this filing's own
 * articles would come down. Confirm is in-app (no native confirm(): that
 * blocks automation), and the plan is fetched before anything is enqueued.
 */
export function ConsolidateControl({ announcementId }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState<ConsolidationLead>('auto');
  const [retireDuplicates, setRetireDuplicates] = useState(true);
  const [result, setResult] = useState<ConsolidationPlan | null>(null);

  const preview = useQuery({
    queryKey: ['consolidation-preview', announcementId, lead],
    queryFn: () => previewConsolidation(announcementId, lead),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => consolidateAnnouncement(announcementId, { lead, retireDuplicates }),
    onSuccess: (plan) => {
      setResult(plan);
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['announcement', announcementId] });
    },
  });

  if (result) {
    return (
      <div className="text-sm text-grey-700">
        <span className="font-semibold">Consolidation queued</span> for{' '}
        {result.entries.map((e) => e.product_name).join(', ')}.{' '}
        {result.retire_duplicates
          ? `${result.entries.reduce((n, e) => n + e.retire.length, 0)} duplicate article(s) will be retired once the rewrite lands.`
          : 'Duplicates left in place.'}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-6 py-2.5 border border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors"
      >
        Consolidate Same-Session
      </button>
    );
  }

  const plan = preview.data;
  const entries = plan?.entries ?? [];
  const socialExposure = entries.reduce(
    (n, e) => n + e.retire.reduce((m, d) => m + d.social_post_count, 0),
    0,
  );

  return (
    <div className="flex flex-col space-y-3 max-w-2xl">
      <p className="text-sm text-grey-700">
        <span className="font-semibold">Consolidate same-session filings:</span> rewrite the
        issuer&apos;s existing article for this session in place, on its original slug and date,
        using every filing from the session. The rewritten article does not post socials again.
      </p>

      {preview.isLoading && <p className="text-sm text-grey-500">Checking the session…</p>}
      {preview.isError && (
        <p className="text-sm text-danger-600">
          Could not load the plan: {(preview.error as any)?.message || 'unknown error'}
        </p>
      )}

      {plan && entries.length === 0 && (
        <p className="text-sm text-grey-600">
          No anchor: the issuer has no other current article on{' '}
          {plan.session_date ?? 'this session'}. Nothing to consolidate into.
        </p>
      )}

      {entries.length > 0 && (
        <table className="text-sm border border-grey-200 rounded-lg overflow-hidden">
          <thead className="bg-grey-50 text-grey-600">
            <tr>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Anchor slug (stays)</th>
              <th className="text-left px-3 py-2">Leads</th>
              <th className="text-left px-3 py-2">Retire</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.product_id} className="border-t border-grey-100">
                <td className="px-3 py-2">{e.product_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.anchor_slug ?? '(unpublished)'}</td>
                <td className="px-3 py-2">
                  {e.lead === 'new' ? 'This filing' : 'Anchor filing'}
                  <span className="block text-xs text-grey-500">{e.lead_reason}</span>
                </td>
                <td className="px-3 py-2">
                  {e.retire.length === 0
                    ? '—'
                    : e.retire.map((d) => (
                        <span key={d.article_id} className="block font-mono text-xs">
                          {d.published_slug ?? d.article_id.slice(0, 8)}
                          {d.social_post_count > 0 && (
                            <span className="text-warning-600"> · {d.social_post_count} social</span>
                          )}
                        </span>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {socialExposure > 0 && (
        <p className="text-sm text-warning-700">
          Retiring these duplicates takes down {socialExposure} article(s) that already have social
          posts pointing at them.
        </p>
      )}

      <div className="flex items-center space-x-4">
        <label className="text-sm text-grey-700 flex items-center space-x-2">
          <span>Lead:</span>
          <select
            value={lead}
            onChange={(e) => setLead(e.target.value as ConsolidationLead)}
            className="border border-grey-300 rounded px-2 py-1 text-sm"
          >
            <option value="auto">Higher substance (auto)</option>
            <option value="new">This filing</option>
            <option value="anchor">Anchor filing</option>
          </select>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={retireDuplicates}
            onChange={(e) => setRetireDuplicates(e.target.checked)}
            className="w-4 h-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-grey-700">Retire this filing&apos;s own duplicates after the rewrite</span>
        </label>
      </div>

      {mutation.isError && (
        <p className="text-sm text-danger-600">
          Consolidation failed: {(mutation.error as any)?.response?.data?.detail || (mutation.error as any)?.message || 'unknown error'}
        </p>
      )}

      <div className="flex items-center space-x-3">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || entries.length === 0}
          className="px-4 py-2 bg-warning-500 hover:bg-warning-600 text-white font-semibold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? 'Queuing…' : 'Confirm'}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-4 py-2 border border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
